import React from 'react';
import * as tf from '@tensorflow/tfjs';

import Board from './board';
import BoardModel from './modules/boardModel';
import { CoordinatesTop, CoordinatesLeft } from './coordinates';
import Config from './modules/config';
import Comment from './comment';
import EngineHelper from './engineHelper';
import FontSize from './fontSize';
import WinRate from './winRate';
import LeelaZeroEngine from './modules/leelaZeroEngine';
import MoveHistory from './moveHistory';
import Parser from './modules/sgf/parser';
import Ponder from './ponder';
import Prediction from './modules/prediction';
import Sgf from './modules/sgf/sgf';
import SgfPanel from './sgfPanel';
import SgfUtil from './modules/sgf/sgfUtil';
import T from './modules/translation';
import Tips from './tips';
import VariationTable from './variationTable';
import ViewMode from './viewMode';
import Message from './message';
import TrialMove from './trialMove';

class Game extends React.Component {

  static widthHeightRatio = 1.05; // Go board h/w ratio
  static gameInfoIdents = ['DT', 'EV', 'RO', 'GN', 'PC',
    'PB', 'BR', 'BT', 'PW', 'WR', 'WT', 'HA', 'KM', 'RE',
    'TM', 'OT', 'RU', 'ON', 'AN', 'US', 'SO', 'CP', 'GC']

  constructor(props) {
    // called twice if <React.StrictMode> is used in index.js.
    // Strict mode checks are run in development mode only;
    // they do not impact the production build.
    super(props);
    this.engine = new LeelaZeroEngine();
    this.backend = undefined;
    // this.landScape = true;
    this.coordinatesWidth = 15;
    this.coordinatesHeight = 12;
    this.boardPadding = 5;
    this.sidePanelMinWidth = 282;
    this.sidePanelMinHeight = 240;
    this.sidePanelMaxWidth = 450;
    this.trialMode = false;
    this.lastBackupTime = 0;
    this.backupInterval = 20 * 1000; // ms.
    this.initBind();
    // window.addEventListener('DOMContentLoaded', (e) => {
    //   this.onLoad();
    // });    
    this.boardSize = 19;
    this.sgf = new Sgf();
    this.gameInfo = {};
    this.savedData = undefined;
    this.state = {
      ponder: true, // do ponder while true
      history: [], // array of tree nodes
      moveNumber: 0, // current move number - node index
      prediction: new Prediction(), // prediction
      winRateBlack: 50.0, // win rate of the current player
      selectedVariation: undefined, // selected variation move
      variationMoves: [], // variation moves
      moveNumbers: this.emptyArray, // move number of each positions
      message: "", // message - ex. "Loading"
      comment: "", // C
      viewMode: "visits", // "visits", "variation", "moveNumber" or "none"
      fontSize: Config.getInstance().fontSize, // squareFontSize = w * fontSize / 20;
      containerStyle: {}, // main container style
      squareWidth: 0, // width of each board square (1 ro)
      gameWidth: 0, // game container width
      gameHeight: 0, // game container height
      sidePanelStyle: {}, // control panel container style
    };
  }

  initBind() {
    this.onLoad = this.onLoad.bind(this);
    this.componentCleanup = this.componentCleanup.bind(this);
    this.pass = this.pass.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.jumpTo = this.jumpTo.bind(this);
    this.goBack = this.goBack.bind(this);
    this.goForward = this.goForward.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.toggleTrialMove = this.toggleTrialMove.bind(this);
    this.togglePonder = this.togglePonder.bind(this);
    this.changeViewMode = this.changeViewMode.bind(this);
    this.selectVariation = this.selectVariation.bind(this);
    this.changeFontSize = this.changeFontSize.bind(this);
    this.onPredict = this.onPredict.bind(this);
    this.onMessage = this.onMessage.bind(this);
    this.onResize = this.onResize.bind(this);
    this.newGame = this.newGame.bind(this);
    this.paste = this.paste.bind(this);
    this.backup = this.backup.bind(this);
    this.restore = this.restore.bind(this);
  }

  componentDidMount() {
    window.onresize = this.onResize;
    window.addEventListener('beforeunload', this.componentCleanup);
    this.onResize();
    this.newGame();
    this.onLoad();
  }

  componentWillUnmount() {
    this.componentCleanup();
    window.removeEventListener('beforeunload', this.componentCleanup);
    window.onresize = (e) => { };
  }

  get emptyArray() {
    return Array(this.boardSize ** 2).fill().map(() => []);
  }

  onLoad() {
    console.log("onLoad()");
    const parsedUrl = new URL(window.location);
    const paramSgf = parsedUrl.searchParams.get('sgf');
    if (paramSgf) {
      const moveNumber = parseInt(parsedUrl.searchParams.get('num')) || 0;
      this.paste(paramSgf, moveNumber);
    }
  }

  componentCleanup() {
    this.backup(true);
    // Chrome requires returnValue to be set
    // e.returnValue = '';
  }

  newGame() {
    this.sgf.newGame(this.boardSize);
    const boardModels = this.createBoardModels();
    this.setState({
      history: boardModels,
      moveNumber: 0, // current move number - slider's value      
      prediction: new Prediction(), // prediction
      winRateBlack: 50.0,
      moveNumbers: this.emptyArray, // move number of each positions
      variationMoves: []
    });
  }

  onResize() {
    const landscape = window.innerWidth - 25 > (window.innerHeight / Game.widthHeightRatio);
    let gameWidth, sidePanelWidth;
    if (landscape) {
      gameWidth = Math.min(
        window.innerWidth - this.sidePanelMinWidth - 15,
        window.innerHeight / Game.widthHeightRatio);
      sidePanelWidth = Math.min(
        window.innerWidth - gameWidth - 15,
        this.sidePanelMaxWidth);
      sidePanelWidth = Math.max(
        sidePanelWidth,
        this.sidePanelMinWidth
      );
    } else {
      gameWidth = Math.min(
        window.innerWidth - 5,
        (window.innerHeight - this.sidePanelMinHeight - 10)
        / Game.widthHeightRatio);
      sidePanelWidth = gameWidth - this.coordinatesWidth;
    }
    const squareWidth = (gameWidth - this.coordinatesWidth
      - this.boardPadding * 2 - 1) / this.boardSize;
    const gameHeight = squareWidth * Game.widthHeightRatio * this.boardSize
      + this.coordinatesHeight + this.boardPadding * 2;
    const containerStyle = {
      flexDirection: landscape ? 'row' : 'column',
      alignItems: landscape ? 'start' : 'center'
    }
    const sidePanelStyle = {
      width: sidePanelWidth + 'px',
      marginLeft: landscape ? '5px' : '15px',
      marginRight: landscape ? '5px' : '0px',
      marginTop: landscape ? '15px' : '5px',
      overflow: 'auto'
    };
    this.setState({
      containerStyle: containerStyle,
      squareWidth: squareWidth,
      gameWidth: gameWidth,
      gameHeight: gameHeight,
      sidePanelStyle: sidePanelStyle,
    });
  }

  createBoardModels() {
    const boardModels = [];
    const boardSize = this.sgf.boardSize;
    let boardModel = new BoardModel(boardSize);
    this.sgf.moveTo(0);
    let node = this.sgf.node;
    this.parseRootNode(node);
    let moveNumber = 0;
    const moveNumbers = this.emptyArray;
    while (node) {
      boardModel = boardModel.clone();
      try {
        if (node.contains("B")) {
          const sgfVertex = node.getProperty("B").value;
          if (sgfVertex && sgfVertex !== "tt") {
            const vertex = SgfUtil.sgfVertexToVertex(sgfVertex);
            boardModel.moveBlack(vertex[0], vertex[1]);
            const index = SgfUtil.sgfVertexToIndex(sgfVertex, boardSize);
            moveNumbers[index].push(moveNumber);
          }
        } else if (node.contains("W")) {
          const sgfVertex = node.getProperty("W").value;
          if (sgfVertex && sgfVertex !== "tt") {
            const vertex = SgfUtil.sgfVertexToVertex(sgfVertex);
            boardModel.moveWhite(vertex[0], vertex[1]);
            const index = SgfUtil.sgfVertexToIndex(sgfVertex, boardSize);
            moveNumbers[index].push(moveNumber);
          }
        }
        if (node.contains("AB")) {
          for (let sgfVertex of node.getProperty("AB").values) {
            if (SgfUtil.isCompressedPoints(sgfVertex)) {
              const sgfVertices = SgfUtil.uncompressPoints(sgfVertex)
              for (let v of sgfVertices) {
                const vertex = SgfUtil.sgfVertexToVertex(v);
                boardModel.addBlack(vertex[0], vertex[1]);
              }
            } else {
              const vertex = SgfUtil.sgfVertexToVertex(sgfVertex);
              boardModel.addBlack(vertex[0], vertex[1]);
            }
          }
        }
        if (node.contains("AW")) {
          for (let sgfVertex of node.getProperty("AW").values) {
            if (SgfUtil.isCompressedPoints(sgfVertex)) {
              const sgfVertices = SgfUtil.uncompressPoints(sgfVertex)
              for (let v of sgfVertices) {
                const vertex = SgfUtil.sgfVertexToVertex(v);
                boardModel.addWhite(vertex[0], vertex[1]);
              }
            } else {
              const vertex = SgfUtil.sgfVertexToVertex(sgfVertex);
              boardModel.addWhite(vertex[0], vertex[1]);
            }
          };
        }
      } catch (error) {
        console.error(error);
      }
      boardModel.blackIsNext = node.isNextBlackTurn();
      boardModels.push(boardModel);
      node = this.sgf.nextNode();
      moveNumber++;
    }
    this.setState({
      moveNumbers: moveNumbers
    });
    this.sgf.moveTo(0);
    this.updateComment(this.sgf.node);
    return boardModels;
  }

  parseRootNode(node) {
    if (!node) {
      return;
    }
    Game.gameInfoIdents.forEach(ident => {
      const p = node.getProperty(ident);
      this.gameInfo[ident] = p ? p.value : undefined;
    })
  }

  updateComment(node) {
    const c = node.getProperty("C");
    this.setState({
      comment: c ? c.value : "",
    });
  }

  onMessage(text) {
    this.setState({
      message: text
    });
  }

  handleClick(index) {
    try {
      this.clear();
      const sgf = this.sgf.sliceTo(this.state.moveNumber + 1);
      sgf.move(SgfUtil.indexToSgfVertex(index, sgf.boardSize));
      this.sgf = sgf;
      const history = this.state.history.slice(0, this.state.moveNumber + 1);
      const boardModel = history[history.length - 1].clone();
      boardModel.move(index);
      history.push(boardModel);
      const moveNumbers = this.updateMoveNumbers(index);
      this.setState(state => ({
        history: history,
        moveNumber: parseInt(state.moveNumber) + 1,
        moveNumbers: moveNumbers
      }));
      this.backup(false);
    } catch (reason) { // illegal move
      console.warn(reason);
      return;
    }
  }

  updateMoveNumbers(index) {
    let moveNumbers = this.state.moveNumbers.slice()
    moveNumbers = moveNumbers.map(arr =>
      arr.filter(value => value !== this.state.moveNumber + 1)
    );
    if (index < this.boardSize ** 2) {
      moveNumbers[index].push(this.state.moveNumber + 1);
      moveNumbers[index] = moveNumbers[index].sort((a, b) => b - a); // desc
    }
    return moveNumbers;
  }

  pass() {
    this.handleClick(this.boardSize ** 2);
  }

  toggleTrialMove(event) {
    if (event.target.checked) {
      this.backup(true);
      this.trialMode = true
    } else {
      this.trialMode = false
      this.restore();
    }
  }

  togglePonder(event) {
    this.setState({
      ponder: event.target.checked
    });
  }

  backup(force) {
    if (this.trialMode) {
      return;
    } 
    if (!force && this.lastBackupTime > Date.now() - this.backupInterval) {
      return      
    }
    this.lastBackupTime = Date.now()
    const config = Config.getInstance();
    config.set("backupSgf", this.sgf.toString());
    config.set("backupMoveNumber", this.state.moveNumber);
    config.set("backupWinRateBlack", this.state.winRateBlack);
    console.log("backup() done");
  }

  restore() {
    const config = Config.getInstance();
    if (config.get("backupSgf")) {
      this.setState({
        winRateBlack: parseFloat(config.set("backupWinRateBlack", 50))
      })
      const source = config.get("backupSgf");
      const moveNumber = parseInt(config.get("backupMoveNumber", 0));
      this.paste(source, moveNumber);
    }
  }

  onPredict(prediction) {
    if (!prediction || this.state.history.length <= this.state.moveNumber) {
      return;
    }
    const node = prediction.node;
    const currentBoardModel = this.state.history[this.state.moveNumber];
    if (node.boardModel.moveNumber !== currentBoardModel.moveNumber) {
      return;
    }
    this.setState({
      prediction: prediction,
      winRateBlack: prediction.winRateBlack,
      variationMoves: []
    });
    const index = (this.state.selectedVariation !== undefined && this.state.viewMode === "variation")
      ? this.state.selectedVariation
      : prediction.bestMoveIndex;
    if (index && !isNaN(index) && node.children[index]) {
      this.selectVariation(index);
    }
    if (!this.backend) {
      // console.log(tf.env().features);
      const floatCapable = tf.ENV.getBool('WEBGL_RENDER_FLOAT32_CAPABLE')
        ? '(float32)' : '(float16)';
      this.backend = (tf.getBackend() ? tf.getBackend().toUpperCase() : "undefined") +
        " " + floatCapable;
    }
  }

  /**
   * Clears prediction and move numbers
   */
  clear() {
    const prediction = new Prediction();
    this.setState({
      prediction: prediction,
      variationMoves: [],
      selectedVariation: undefined
    });
  }

  jumpTo(event) {
    this.goTo(parseInt(event.target.value));
  }

  goTo(moveNumber) {
    this.clear();
    this.sgf.moveTo(moveNumber);
    this.updateComment(this.sgf.node);
    if (moveNumber === this.state.moveNumber + 1) {
      this.backup(false);
    }
    this.setState({
      moveNumber: moveNumber,
      variationMoves: [],
      selectedVariation: undefined
    });
  }

  goBack(e) {
    if (e) {
      e.preventDefault();
    }
    if (this.state.moveNumber > 0) {
      this.goTo(this.state.moveNumber - 1);
    }
  }

  goForward(e) {
    if (e) {
      e.preventDefault();
    }
    if (this.state.moveNumber < this.state.history.length - 1) {
      this.goTo(this.state.moveNumber + 1);
    }
  }

  onWheel(e) {
    if (e.target instanceof HTMLTextAreaElement) {
      return;
    }
    e.deltaY > 0 ? this.goForward() : this.goBack();
  }

  changeViewMode(event) {
    const mode = event.target.value;
    if (mode !== "variation") {
      this.setState({ variationMoves: [] });
    }
    this.setState({
      viewMode: mode
    });
  }

  selectVariation(index) {
    const prediction = this.state.prediction;
    if (!prediction || !prediction.node) {
      return;
    }
    const node = prediction.node;
    if (node.children[index]) {
      const sequence = node.children[index].getBestSequence();
      const moves = sequence.map(n => n.boardModel.lastMove);
      this.setState({
        selectedVariation: index,
        variationMoves: moves
      });
    }
  }

  changeFontSize(event) {
    const size = parseInt(event.target.value);
    Config.getInstance().fontSize = size;
    this.setState({
      fontSize: parseInt(size)
    });
  }

  paste(source, moveNumber = 0) {
    try {
      this.clear();
      this.sgf = new Parser().parse(source);
      const boardModels = this.createBoardModels();
      this.setState({
        history: boardModels,
        moveNumber: moveNumber,
        winRateBlack: 50.0
      }, () => {
        document.querySelector('#main-tab').click();
        this.goTo(moveNumber);
        this.backup(true);
        if (!this.state.ponder) {
          document.querySelector('#ponder').click();
        }
        if (window.history.pushState) {
          const loc = document.location;
          const newUrl = loc.protocol + "//" + loc.host + loc.pathname
            + "?sgf=" + encodeURI(this.sgf)
            + "&num=" + moveNumber;
          window.history.pushState(null, '', newUrl);
        }
      });
    } catch (reason) {
      alert(reason);
    }
  }

  getCoordinatesTop() {
    return (
      <div className="coordinatesTop"
        style={{ width: this.state.gameWidth }}>
        <CoordinatesTop
          boardSize={this.boardSize}
          width={this.state.squareWidth}
          height={this.coordinatesWidth}
          fontSize={this.state.fontSize}
        />
      </div>
    );
  }

  getCoordinatesLeft() {
    return (
      <div className="coordinatesLeft"
        style={{ height: this.state.gameHeight - this.coordinatesHeight }}>
        <CoordinatesLeft
          boardSize={this.boardSize}
          width={this.coordinatesWidth}
          height={this.state.squareWidth * Game.widthHeightRatio}
          fontSize={this.state.fontSize}
        />
      </div>
    );
  }

  getBoard(boardModel) {
    return (
      <Board
        boardModel={boardModel}
        onClick={this.handleClick}
        prediction={this.state.prediction}
        moveNumber={this.state.moveNumber}
        moveNumbers={this.state.moveNumbers}
        variationMoves={this.state.variationMoves}
        viewMode={this.state.viewMode}
        squareWidth={this.state.squareWidth}
        squareHeight={this.state.squareWidth * Game.widthHeightRatio}
        fontSize={this.state.fontSize}
      />
    );
  }

  getControlPanel(boardModel) {
    return (
      <div>
        <ul id="myTabs" className="nav nav-tabs" role="tablist">
          <li role="presentation" className="nav-item active">
            <button className="nav-link active" id="main-tab" data-bs-toggle="tab"
              data-bs-target="#main" type="button" role="tab" aria-controls="main"
              aria-selected="true"><i className="bi bi-house"></i></button>
          </li>
          <li role="presentation" className="nav-item">
            <button className="nav-link" id="game-info-tab" data-bs-toggle="tab"
              data-bs-target="#game-info" type="button" role="tab" aria-controls="game-info"
              aria-selected="false"><i className="bi bi-info-lg"></i></button>
          </li>
          <li role="presentation" className="nav-item">
            <button className="nav-link" id="sgf-tab" data-bs-toggle="tab"
              data-bs-target="#sgf" type="button" role="tab" aria-controls="sgf"
              aria-selected="false"><i className="bi bi-file-text"></i></button>
          </li>
          <li role="presentation" className="nav-item">
            <button className="nav-link" id="config-tab" data-bs-toggle="tab"
              data-bs-target="#config" type="button" role="tab" aria-controls="config"
              aria-selected="false"><i className="bi bi-gear"></i></button>
          </li>
          <li role="presentation" className="nav-item">
            <button className="nav-link" id="link-tab" data-bs-toggle="tab"
              data-bs-target="#link" type="button" role="tab" aria-controls="link"
              aria-selected="false"><i className="bi bi-bookmark-star"></i></button>
          </li>
        </ul>

        <div className="tab-content">
          {this.getMainTabPane(boardModel)}
          {this.getGameInfoPane(boardModel)}
          {this.getSgfTabPane()}
          {this.getConfigTabPane()}
          {this.getLinkTabPane()}
        </div>
      </div>
    );
  }

  getMainTabPane(boardModel) {
    const speed = (this.state.prediction && this.state.ponder)
      ? this.state.prediction.speed
      : 0;
    return (
      <div role="tabpanel" className="tab-pane fade show active" id="main" aria-labelledby="main-tab">
        <WinRate
          blackIsNext={boardModel ? boardModel.blackIsNext : true}
          winRateBlack={this.state.winRateBlack}
          gameInfo={this.gameInfo}
          captureBlack={boardModel ? boardModel.deadWhite : 0}
          captureWhite={boardModel ? boardModel.deadBlack : 0}
        />
        <MoveHistory
          moveNumber={this.state.moveNumber}
          maxMoveNumber={this.state.history.length
            ? this.state.history.length - 1 : 0}
          onChange={this.jumpTo}
          goBack={this.goBack}
          goForward={this.goForward}
        />
        <div className="ponder-container mt-4">
          <button className="btn btn-outline-secondary btn-sm pass-button"
            type="submit" onClick={this.pass}>
            {T.get("pass")}
          </button>
          <TrialMove onChange={this.toggleTrialMove} />
          <Ponder onChange={this.togglePonder} speed={speed} />
        </div>
        <ViewMode
          onChange={this.changeViewMode}
          viewMode={this.state.viewMode} />
        <VariationTable
          boardSize={this.boardSize}
          prediction={this.state.prediction}
          selected={this.state.selectedVariation}
          viewMode={this.state.viewMode}
          onChange={this.selectVariation}
        />
        <Comment comment={this.state.comment} />
        <Tips />
      </div>
    );
  }

  getGameInfoPane() {
    const rows = [];
    for (let ident in this.gameInfo) {
      if (this.gameInfo[ident]) {
        rows.push(
          <tr key={ident}>
            <td>{T.get("gameInfo" + ident)}</td>
            <td>{this.gameInfo[ident]}</td>
          </tr>
        )
      }
    }
    return (
      <div role="tabpanel" className="tab-pane fade" id="game-info" aria-labelledby="game-info-tab">
        <div className="table-responsive">
          <table className="game-info-table table table-bordered table-condensed">
            <tbody>
              {rows}
            </tbody>
          </table>
        </div>
      </div>
    ) 
  }

  getSgfTabPane() {
    return (
      <div role="tabpanel" className="tab-pane fade" id="sgf" aria-labelledby="sgf-tab">
        <SgfPanel
          sgf={this.sgf ? this.sgf.toString() : ""}
          moveNumber={this.state.moveNumber}
          onNewGame={this.newGame}
          onPaste={this.paste}
          onRestore={this.restore}
        />
      </div>
    );
  }

  getConfigTabPane() {
    return (
      <div role="tabpanel" className="tab-pane fade" id="config" aria-labelledby="config-tab">
        <div className="card config-panel">
          <div className="card-body">
            {this.getFontSize()}
            {this.getEngineHelper()}
          </div>
        </div>
      </div>
    );
  }

  getFontSize() {
    return (
      <FontSize
        fontSize={this.state.fontSize}
        onChange={this.changeFontSize}
      />
    );
  }

  getEngineHelper() {
    const history = this.state.history.length
      ? this.state.history.slice(0, this.state.moveNumber + 1)
      : [];
    return (
      <EngineHelper
        engine={this.engine}
        backend={this.backend}
        history={history}
        ponder={this.state.ponder}
        onPredict={this.onPredict}
        onMessage={this.onMessage}
      />
    );
  }

  getLinkTabPane() {
    return (
      <div role="tabpanel" className="tab-pane fade" id="link" aria-labelledby="link-tab">
        <div className="list-group">
          <a href="https://fujigoban.appspot.com/"
            className="list-group-item list-group-item-action" target="FujiGobanHome" rel="noreferrer">
            {T.get("linkHome")}
          </a>
          <a href="https://fujigoban.appspot.com/feed"
            className="list-group-item list-group-item-action" target="FujiGobanHome" rel="noreferrer">
            {T.get("linkNews")}
          </a>
          <a href="https://fujigoban.appspot.com/tweets"
            className="list-group-item list-group-item-action" target="FujiGobanHome" rel="noreferrer">
            {T.get("linkTweets")}
          </a>
          <a href="https://fujigoban.appspot.com/youtube/igo"
            className="list-group-item list-group-item-action" target="FujiGobanHome" rel="noreferrer">
            {T.get("linkYouTube")}
          </a>
          <a href="https://fujigoban.appspot.com/book/igo"
            className="list-group-item list-group-item-action" target="FujiGobanHome" rel="noreferrer">
            {T.get("linkBook")}
          </a>
          <a href="https://twitter.com/FujiGoban"
            className="list-group-item list-group-item-action" target="FujiGobanHome" rel="noreferrer">
            Release Notes
          </a>
          <a href="./notice.html"
            className="list-group-item list-group-item-action" target="FujiGobanHome" rel="noreferrer">
            NOTICE
          </a>
        </div>
      </div>
    );
  }

  render() {
    const boardModel =
      (this.state.history.length > this.state.moveNumber)
        ? this.state.history[this.state.moveNumber]
        : undefined;

    return (
      <div className="flex-container"
        onWheel={this.onWheel}
        style={this.state.containerStyle}>
        <div className="game" style={{
          width: this.state.gameWidth,
          height: this.state.gameHeight
        }}>
          {this.getCoordinatesTop()}
          {this.getCoordinatesLeft()}
          {this.getBoard(boardModel)}
        </div>
        <div className="side-panel" style={this.state.sidePanelStyle}>
          <div className="card control-panel">
            <div className="card-body">
              {this.getControlPanel(boardModel)}
            </div>
          </div>
          <Message text={this.state.message} />
        </div>
      </div >
    );
  }
}

export default Game;